/* eslint-disable default-case */
import React, { useEffect, useMemo, useState } from "react";
import Select from 'react-select';
import { useDropzone } from 'react-dropzone';
import { isEmpty } from "lodash";

import selectStyles from '../../../util/selectStyles';
import PreviewFile from "../../shared/PreviewFile";
import TextWithVariables from "./TextWithVariables";
import AdvancedActions from "./AdvancedActions";
import InactivityTimeoutConfig from "./InactivityTimeoutConfig";
import IconMC from '../../../presentation/components/IconMC';
import { validateMediaByPlatforms } from "../../../util/validateMediaByPlatforms";
import {
  chatbotDisallowsDocuments,
  getEnabledMediaChannels
} from "./utils";

const buildLabelTypeOption = (type) => {
  let icon;
  let text;
  let message;

  switch (type) {
    case 'text':
      icon = 'text-outline'; text = 'Texto'; message = 'Mensaje de texto, puede contener variables.';
      break;
    case 'image':
      icon = 'image-outline'; text = 'Imagen'; message = 'Envía una imagen, PNG o JPG';
      break;
    case 'document':
      icon = 'file-pdf'; text = 'Documento'; message = 'Envía un archivo PDF';
      break;
    case 'video':
      icon = 'video-outline'; text = 'Video'; message = 'Envía un video MP4';
      break;
    case 'audio':
      icon = 'mic-outline'; text = 'Audio'; message = 'Envía un audio mp3';
      break;
    default:
      icon = ''; text = ''; message = '';
  }

  return (
    <div className="d-flex flex-row">
      <div className="align-self-center mr-10">
        <IconMC name={icon} size="base" />
      </div>
      <div className="d-flex flex-column align-self-center">
        <span className="fz-11">{text}</span>
        <span className="fz-11">{message}</span>
      </div>
    </div>
  )
}

const ALL_MESSAGE_TYPE_OPTIONS = [
  { value: 'text', label: buildLabelTypeOption('text') },
  { value: 'image', label: buildLabelTypeOption('image') },
  { value: 'document', label: buildLabelTypeOption('document') },
  { value: 'audio', label: buildLabelTypeOption('audio') },
  { value: 'video', label: buildLabelTypeOption('video') }
];

const MessageOption = ({
  chatbot,
  chatbotOption,
  setChatbotOption,
  errors,
  setErrors,
  customerColumns
}) => {
  const [acceptFileTypes, setAcceptFileTypes] = useState('');
  const [labelDropzone, setLabelDropzone] = useState('');
  const [imageSize, setImageSize] = useState(null);
  const [initialData, setInitialData] = useState({});

  const enabledChannels = useMemo(() => getEnabledMediaChannels(chatbot), [chatbot]);
  const messageTypeOptions = useMemo(() => {
    if (!chatbotDisallowsDocuments(chatbot)) return ALL_MESSAGE_TYPE_OPTIONS;
    return ALL_MESSAGE_TYPE_OPTIONS.filter((opt) => opt.value !== 'document');
  }, [chatbot]);

  useEffect(() => {
    const { message_type, attached_file } = chatbotOption;

    setInitialData({ message_type, attached_file });

    if (!isEmpty(attached_file)) {
      setImageSize({
        width: attached_file.metadata?.width,
        height: attached_file.metadata?.height
      });
    };
  }, []);

  useEffect(() => {
    if (!chatbotOption.message_type) return;

    handleAcceptFileTypes();
  }, [chatbotOption.message_type]);

  const updateInfo = (name, value) => {
    setChatbotOption({
      ...chatbotOption,
      [name]: value
    });
  }

  const handleSelect = (option) => {
    const { message_type, attached_file } = initialData;

    if (message_type == option.value && !isEmpty(attached_file)) {
      setChatbotOption({
        ...chatbotOption,
        message_type,
        answer: '',
        attached_file,
        selectedFile: null
      });
    } else {
      setChatbotOption({
        ...chatbotOption,
        answer: '',
        message_type: option.value,
        attached_file: null,
        selectedFile: null
      });
    }
  }

  const handleImageSize = (file) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.src = url;
    img.onload = () => {
      setImageSize({
        width: img.width,
        height: img.height,
      });
    };
  }

  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];

    if (!file) return;

    let isValidFile = true;
    let message = '';

    switch (chatbotOption.message_type) {
      case 'image':
        if (['image/jpg, image/jpeg, image/png'].includes(file.type)) {
          message = 'Selecciona un archivo: jpg, jpeg o png';
          isValidFile = false;
        }
        break;
      case 'document':
        if (file.type != 'application/pdf') {
          message = 'Selecciona un archivo PDF';
          isValidFile = false;
        }
        break;
      case 'audio':
        if (file.type != 'audio/mpeg') {
          message = 'Selecciona un audio mpeg';
          isValidFile = false;
        }
        break;
      case 'video':
        if (['video/mp4, video/3gpp'].includes(file.type)) {
          message = 'Selecciona un video mp4 o 3gpp';
          isValidFile = false;
        }
        break;
    }

    if (isValidFile) {
      const { valid, error } = validateMediaByPlatforms(file, enabledChannels);
      isValidFile = valid;
      message = error;
    }

    if (!isValidFile) {
      setErrors({ ...errors, file_error: message });
      return;
    }

    handleImageSize(file);
    setInitialData({});
    setChatbotOption({
      ...chatbotOption,
      attached_file: null,
      selectedFile: file
    });
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: acceptFileTypes,
    maxFiles: 1,
    inputContent: ''
  });

  const handleAcceptFileTypes = () => {
    if (chatbotOption.message_type == 'text') {
      setLabelDropzone('');
      setAcceptFileTypes('');
      return;
    }

    let accept;
    let label;

    switch (chatbotOption.message_type) {
      case 'image':
        accept = 'image/jpg, image/jpeg, image/png';
        label = 'Arrastra tu imagen';
        break;
      case 'document':
        accept = 'application/pdf';
        label = 'Arrastra tu archivo PDF';
        break;
      case 'video':
        accept = 'video/mp4, video/3gpp';
        label = 'Arrastra tu video';
        break;
      case 'audio':
        accept = 'audio/mpeg', 'audio/mp3';
        label = 'Arrastra tu audio';
        break;
      default:
        accept = '';
        label = '';
    }

    setLabelDropzone(label);
    setAcceptFileTypes(accept);
  }

  const renderLegend = () => {
    if (!['image', 'video'].includes(chatbotOption.message_type)) return false;

    return (
      <div className="col-md-12 mt-10">
        <div className="d-flex flex-column bg-light-cian p-10 border-12 fz-11">
          <span className="text-blue">Esta es solo una vista previa de tu mensaje. Los archivos enviados no serán alterados ni modificados.</span>
        </div>
      </div>
    )
  }

  const renderFileName = () => {
    const { selectedFile, attached_file } = chatbotOption;

    if (selectedFile) return selectedFile.name;
    if (!isEmpty(attached_file)) return attached_file.filename;

    return '';
  }

  const removeSelectedFile = () => {
    setInitialData({});
    setChatbotOption({
      ...chatbotOption,
      attached_file: null,
      selectedFile: null
    });
  }

  return (
    <>
      <div className="col-md-12 mt-10">
        <div className="figma-field mb-0">
          <label>Tipo de mensaje</label>
          <span className="ml-10 error-msg fz-12">{errors.message_type}</span>
          <Select
            options={messageTypeOptions}
            placeholder="Selecciona"
            className={chatbotOption.message_type ? '' : 'select-tag'}
            classNamePrefix={chatbotOption.message_type ? '' : 'select-tag'}
            styles={selectStyles}
            onChange={handleSelect}
            value={[messageTypeOptions.find((el) => el.value == chatbotOption.message_type)]}
            name="message_type"
            components={{
              IndicatorSeparator: () => null
            }}
          />
        </div>
      </div>

      {['text', 'image', 'video', 'document'].includes(chatbotOption.message_type) && (
        <TextWithVariables
          chatbotOption={chatbotOption}
          setChatbotOption={setChatbotOption}
          errors={errors}
          customerColumns={customerColumns}
          maxChars={chatbot.max_size_text} />
      )}

      {!chatbotOption.selectedFile && isEmpty(chatbotOption.attached_file) && ['image', 'document', 'video', 'audio'].includes(chatbotOption.message_type) && (
        <div {...getRootProps()} className="cursor-pointer col-md-12">
          <input {...getInputProps()} accept={acceptFileTypes} />
          <div className="template-attach-container mt-25">
            <div className="d-flex flex-column justify-content-center">
              <span className="tw-flex tw-justify-center tw-text-gray-600">
                <IconMC name="attach-fill" size="2xl" />
              </span>
              <p className="upload-text fz-14 mt-10">{labelDropzone} o haz <span className="text-blue">click aquí</span></p>
            </div>
          </div>
        </div>
      )}

      {errors.file_error && !chatbotOption.selectedFile && isEmpty(chatbotOption.attached_file) && (
        <div className="col-md-12 mt-10">
          <span className="ml-10 error-msg fz-12">{errors.file_error}</span>
        </div>
      )}

      {(chatbotOption.selectedFile || !isEmpty(chatbotOption.attached_file)) && (
        <>
          <div className="col-md-12 mt-20 d-flex justify-content-between">
            <span className='template-file-selected d-flex align-items-center'>{renderFileName()}</span>
            <button
              type="button"
              className="remove-file-container tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0"
              onClick={removeSelectedFile}
              aria-label="Eliminar archivo"
            >
              <IconMC name="trash-2-outline" size="base" />
            </button>
          </div>

          {renderLegend()}

          {chatbotOption.message_type !== 'text' && (
            <div className='col-md-12 mt-20 d-flex justify-content-center'>
              <div className={`template-example ${chatbotOption.message_type == 'audio' ? 'w-100' : ''}`}>
                <PreviewFile
                  selectedFile={chatbotOption.selectedFile}
                  imageSize={imageSize}
                  attachedFile={chatbotOption.attached_file} />
                {(chatbotOption.answer) && (
                  <div className="template-text-description">{chatbotOption.answer}</div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      <div className="mt-20">
        <AdvancedActions
          chatbot={chatbot}
          chatbotOption={chatbotOption}
          setChatbotOption={setChatbotOption}
          customerColumns={customerColumns}
          errors={errors}
        />
      </div>

      <div className="mt-20">
        <InactivityTimeoutConfig
          chatbotOption={chatbotOption}
          setChatbotOption={setChatbotOption}
          errors={errors}
        />
      </div>
    </>
  );
};

export default MessageOption;
